/**
 * Why the last call didn't go out, inside the dialer (desktop GlobalDialer +
 * mobile dialer), with the one thing to do about it. See lib/dialBlock.
 */
import { useState } from 'react';
import { LifeBuoy, ShieldCheck } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { dialBlockView, type DialBlock } from '@/lib/dialBlock';
import { startIdentityVerification } from '@/lib/dialerCountryGate';

export const NOTICE_CLASS = 'rounded-xl border border-brand-lime bg-brand-lime-soft p-3 text-xs text-brand-ink dark:text-brand-lime-soft-foreground';
export const NOTICE_BUTTON_CLASS = 'inline-flex items-center gap-1.5 min-h-[36px] rounded-full px-3 py-1.5 font-semibold text-brand-ink bg-brand-lime hover:bg-brand-lime-hover disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** The button that opens the support form for a blocked country. */
export function SupportButton({ label, href }: { label: string; href: string }) {
  return (
    <a href={href} className={NOTICE_BUTTON_CLASS} data-testid="link-dial-block-support">
      <LifeBuoy className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {label}
    </a>
  );
}

/** A quiet second route under a primary action. */
export function SupportAlternative({ label, href }: { label: string; href: string }) {
  return (
    <a href={href} className="underline underline-offset-2" data-testid="link-dial-block-alternative">
      {label}
    </a>
  );
}

export function DialBlockNotice({ block, kycVerified }: { block: DialBlock; kycVerified: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const view = dialBlockView(block, { kycVerified });

  const verify = async () => {
    setBusy(true);
    setErr(null);
    const r = await startIdentityVerification((url, body) => apiRequest('POST', url, body));
    setBusy(false);
    if (r.url) window.location.href = r.url;
    else setErr(r.error || 'Could not start verification.');
  };

  return (
    <div className={NOTICE_CLASS} role="alert" data-testid={`dial-block-${block.code}`}>
      <p className="leading-snug">{view.message}</p>
      {(view.cta || view.alternative) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {view.cta?.kind === 'verify' && (
            <button type="button" onClick={verify} disabled={busy} className={NOTICE_BUTTON_CLASS} data-testid="button-dial-block-verify">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              {busy ? 'Opening…' : view.cta.label}
            </button>
          )}
          {view.cta?.kind === 'link' && <SupportButton label={view.cta.label} href={view.cta.href} />}
          {view.alternative && <SupportAlternative label={view.alternative.label} href={view.alternative.href} />}
          {err && <span className="text-destructive">{err}</span>}
        </div>
      )}
    </div>
  );
}
